// Utilidades para manejo de archivos

// Obtener extensión de archivo
export const getFileExtension = (fileName) => {
  return fileName.slice((fileName.lastIndexOf(".") - 1 >>> 0) + 2).toLowerCase();
};

// Obtener nombre sin extensión
export const getFileNameWithoutExtension = (fileName) => {
  return fileName.replace(/\.[^/.]+$/, "");
};

// Formatear tamaño de archivo
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Formatear fecha
export const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Obtener tipo de archivo por extensión
export const getFileType = (fileName) => {
  const extension = getFileExtension(fileName);
  
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'ico', 'avif'];
  const rawImageExtensions = ['cr3', 'cr2', 'crw', 'arw', 'srf', 'sr2', 'nef', 'nrw', 'dng', 'orf', 'rw2', 'pef', 'raf', 'x3f', '3fr', 'erf', 'mef', 'mos', 'mrw', 'dcr', 'kdc', 'rwl'];
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'ogv', '3gp'];
  const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma', 'm4a', 'opus'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'md', 'log'];
  const spreadsheetExtensions = ['xls', 'xlsx', 'csv', 'ods'];
  const presentationExtensions = ['ppt', 'pptx', 'odp'];
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz'];
  const codeExtensions = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'json', 'xml', 'py', 'java', 'cpp', 'c', 'h', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'scala', 'sql', 'sh', 'bat', 'yml', 'yaml', 'toml', 'ini', 'conf'];

  // Combinar imágenes normales y RAW bajo el tipo 'image'
  if (imageExtensions.includes(extension) || rawImageExtensions.includes(extension)) return 'image';
  if (videoExtensions.includes(extension)) return 'video';
  if (audioExtensions.includes(extension)) return 'audio';
  if (documentExtensions.includes(extension)) return 'document';
  if (spreadsheetExtensions.includes(extension)) return 'spreadsheet';
  if (presentationExtensions.includes(extension)) return 'presentation';
  if (archiveExtensions.includes(extension)) return 'archive';
  if (codeExtensions.includes(extension)) return 'code';
  
  return 'file';
};

// Verificar si es una carpeta
export const isFolder = (item) => {
  return item.type === 'folder' || item.Key?.endsWith('/') || item.isVirtualFolder;
};

// Extraer carpetas virtuales desde prefijos de archivos
export const extractVirtualFolders = (files, currentPath = '') => {
  const virtualFolders = new Set();
  const realFolders = new Set();
  const normalizedCurrentPath = currentPath === '' ? '' : currentPath.endsWith('/') ? currentPath : currentPath + '/';
  
  // Primero identificar carpetas reales (que terminan en /) en el nivel actual
  files.forEach(file => {
    const fileName = file.Key || file.name || '';
    if (fileName.endsWith('/')) {
      // Solo considerar carpetas en el nivel actual
      if (normalizedCurrentPath === '') {
        // En la raíz, solo carpetas de primer nivel (sin slashes en el nombre)
        const folderName = fileName.slice(0, -1); // remover el slash final
        if (!folderName.includes('/')) {
          realFolders.add(folderName);
        }
      } else {
        // En subcarpetas, solo las que están directamente en el path actual
        if (fileName.startsWith(normalizedCurrentPath)) {
          const relativePath = fileName.substring(normalizedCurrentPath.length);
          const folderName = relativePath.slice(0, -1); // remover el slash final
          if (folderName && !folderName.includes('/')) {
            realFolders.add(folderName);
          }
        }
      }
    }
  });
  
  // Luego extraer carpetas virtuales del nivel actual, evitando duplicados con carpetas reales
  files.forEach(file => {
    const fileName = file.Key || file.name || '';
    
    // Solo procesar archivos que están en el path actual o son subpaths
    if (normalizedCurrentPath && !fileName.startsWith(normalizedCurrentPath)) {
      return;
    }
    
    // Remover el path actual del filename
    const relativePath = normalizedCurrentPath ? fileName.substring(normalizedCurrentPath.length) : fileName;
    
    // Si hay un slash, extraer solo el primer nivel de carpeta
    const slashIndex = relativePath.indexOf('/');
    if (slashIndex > 0) {
      const folderName = relativePath.substring(0, slashIndex);
      
      // Solo agregar si:
      // 1. No es una carpeta real
      // 2. Es del nivel actual (no tiene más slashes después)
      if (!realFolders.has(folderName)) {
        virtualFolders.add(folderName);
      }
    }
  });
  
  // Convertir Set a array de objetos carpeta
  return Array.from(virtualFolders).map(folderName => ({
    Key: normalizedCurrentPath + folderName + '/',
    displayName: folderName,
    type: 'folder',
    isVirtualFolder: true,
    Size: 0,
    LastModified: new Date().toISOString()
  }));
};

// Convertir archivo a base64
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remover el prefijo "data:type/subtype;base64,"
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

// Validar tipos de archivo permitidos
export const isValidFileType = (file, allowedTypes = []) => {
  if (allowedTypes.length === 0) return true;
  
  const fileType = getFileType(file.name);
  return allowedTypes.includes(fileType);
};

// Validar tamaño de archivo
export const isValidFileSize = (file, maxSizeInMB = 50) => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

// Filtrar archivos por búsqueda
export const filterFiles = (files, searchTerm, selectedType = 'all') => {
  if (!searchTerm && selectedType === 'all') return files;

  return files.filter(file => {
    const fileName = file.Key || file.name;
    
    // Búsqueda mejorada: dividir en palabras y buscar todas
    const matchesSearch = !searchTerm || (() => {
      const fileNameLower = fileName?.toLowerCase() || '';
      const searchWords = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length > 0);
      return searchWords.every(word => fileNameLower.includes(word));
    })();
    
    let matchesType = false;
    
    switch (selectedType) {
      case 'all':
        matchesType = true;
        break;
      case 'folder':
        matchesType = isFolder(file);
        break;
      case 'image':
        // Solo imágenes que NO son RAW
        matchesType = isImage(fileName) && !isRawImage(fileName);
        break;
      case 'raw':
        // Solo archivos RAW
        matchesType = isRawImage(fileName);
        break;
      default:
        // Para otros tipos (video, audio, document, etc.)
        matchesType = getFileType(fileName) === selectedType;
        break;
    }
    
    return matchesSearch && matchesType;
  });
};

// Ordenar archivos
export const sortFiles = (files, sortBy = 'name', sortOrder = 'asc') => {
  const sorted = [...files].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = (a.Key || a.name || '').toLowerCase();
        bValue = (b.Key || b.name || '').toLowerCase();
        break;
      case 'size':
        aValue = a.Size || a.size || 0;
        bValue = b.Size || b.size || 0;
        break;
      case 'date':
        aValue = new Date(a.LastModified || a.lastModified || 0);
        bValue = new Date(b.LastModified || b.lastModified || 0);
        break;
      case 'type':
        aValue = getFileType(a.Key || a.name || '');
        bValue = getFileType(b.Key || b.name || '');
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Poner carpetas primero si están ordenando por nombre
  if (sortBy === 'name') {
    return sorted.sort((a, b) => {
      const aIsFolder = isFolder(a);
      const bIsFolder = isFolder(b);
      
      if (aIsFolder && !bIsFolder) return -1;
      if (!aIsFolder && bIsFolder) return 1;
      return 0;
    });
  }
  
  return sorted;
};

// Verificar si es una imagen (incluyendo RAW)
export const isImage = (fileName) => {
  return getFileType(fileName) === 'image';
};

// Verificar si es un archivo RAW
export const isRawImage = (fileName) => {
  const extension = getFileExtension(fileName);
  const rawImageExtensions = ['cr3', 'cr2', 'crw', 'arw', 'srf', 'sr2', 'nef', 'nrw', 'dng', 'orf', 'rw2', 'pef', 'raf', 'x3f', '3fr', 'erf', 'mef', 'mos', 'mrw', 'dcr', 'kdc', 'rwl'];
  return rawImageExtensions.includes(extension);
};

// Verificar si puede generar thumbnail (imágenes comunes, no RAW)
export const canGenerateThumbnail = (fileName) => {
  const extension = getFileExtension(fileName);
  const thumbnailSupportedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff'];
  return thumbnailSupportedExtensions.includes(extension);
};

// Capitalizar tag: primera letra de cada palabra en mayúscula
export const capitalizeTag = (tag) => {
  if (!tag || typeof tag !== 'string') return tag;
  // Si el tag tiene guiones, capitalizar cada palabra separada por guión
  if (tag.includes('-')) {
    return tag
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  // Si no tiene guiones, capitalizar solo la primera letra
  return tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase();
}; 